import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import indexOf from 'lodash/indexOf';
import some from 'lodash/some';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import moment from 'moment-timezone';
import Button from '@spothero/ui/button';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import StorageUtils from '@spothero/utils/storage';
import GTMUtils from 'utils/gtm';
import SHUtils from 'utils/sh-utils';
import CreditCardAPI from 'api/credit-card';
import withConfig from 'config/withConfig';
import SHLoader from 'common/loader/SHLoader';
import Card from 'common/card/Card';
import FullPageLoader from 'common/loader/FullPageLoader';
import {
    CHECKOUT_ERROR_DATA,
    PROMO_CODE_DATA,
} from 'checkout/checkout-prop-types';
import {USER_DATA} from 'user/user-prop-types';
import {push} from 'router/router-actions';
import {userUpdate} from 'user/user-actions';
import UserUtils from 'user/user-utils';
import SegmentUtils from 'utils/segment';
import {
    clearCustomer,
    fetchRates,
    showChangeDateTime,
    hideChangeDateTime,
    hideRecommendedSpots,
    hideRedundantMonthlyDetails,
    toggleTimeExtendedDetails,
    updateData,
    purchase,
} from './checkout-actions';
import {
    hideRateDetails,
    toggleOnlineCommuterDetails,
    toggleOversizedFeeDetails,
    resetSelectedRate,
} from 'spot/spot-actions';
import CheckoutMeta from './CheckoutMeta';
import AccountInformation from './account/AccountInformation';
import ReservationDetails from './reservation/ReservationDetails';
import PaymentInformation from './payment/PaymentInformation';
import ReservationSummary from './summary/ReservationSummary';
import CheckoutValueProps from './common/CheckoutValueProps';
import CheckoutUtils from './checkout-utils';

const ChangeDateTimeModal = loadable(() =>
    import('common/date-time-pickers/ChangeDateTimeModal')
);
const WhyTimeExtendedModal = loadable(() =>
    import('common/extended-time/WhyTimeExtendedModal')
);
const RateDetailsModal = loadable(() =>
    import('spot-details/modals/RateDetailsModal')
);
const OnlineCommuterRateModal = loadable(() =>
    import('spot-details/modals/OnlineCommuterRateModal')
);
const OversizedFeeDetailsModal = loadable(() =>
    import('spot-details/modals/OversizedFeeDetailsModal')
);
const RecommendedSpotsModal = loadable(() => import('./RecommendedSpotsModal'));
const RedundantMonthlyReservationModal = loadable(() =>
    import('./RedundantMonthlyReservationModal')
);

class Checkout extends Component {
    static propTypes = {
        isLoadingRates: PropTypes.bool.isRequired,
        isPurchasing: PropTypes.bool.isRequired,
        isPurchasingWithPaymentRequest: PropTypes.bool,
        apiDateTimeFormat: PropTypes.string.isRequired,
        checkoutError: PropTypes.shape(CHECKOUT_ERROR_DATA),
        user: PropTypes.shape(USER_DATA).isRequired,
        selectedCreditCard: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        match: PropTypes.shape({
            params: PropTypes.shape({
                spotId: PropTypes.string,
            }),
        }),
        spot: PropTypes.object,
        isMonthly: PropTypes.bool,
        isAirport: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        rid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        promoCode: PropTypes.shape(PROMO_CODE_DATA),
        eventId: PropTypes.number,
        phoneNumber: PropTypes.string,
        phoneNumberFriendly: PropTypes.string,
        monthlyPhoneNumber: PropTypes.string,
        monthlyPhoneNumberFriendly: PropTypes.string,
        cityDisplayName: PropTypes.string,
        cityTimezone: PropTypes.string,
        previousRental: PropTypes.bool,
        showChangeDateTimeModal: PropTypes.bool,
        recommendedSpotsUnavailableReason: PropTypes.string,
        showRecommendedSpotsModal: PropTypes.bool,
        showRedundantMonthlyReservationModal: PropTypes.bool,
        redundantMonthlyReservationModalDate: PropTypes.string,
        redundantMonthlyReservationModalParkingPassURL: PropTypes.string,
        showRateDetailsModal: PropTypes.bool,
        rateDetailsModalSection: PropTypes.string,
        showOversizedFeeDetailsModal: PropTypes.bool,
        showOnlineCommuterRateModal: PropTypes.bool,
        showWhyTimeExtendedModal: PropTypes.bool,
        purchaseForCustomer: PropTypes.bool,
        isRebookingReservation: PropTypes.bool,
        changeDateTimeModalTitle: PropTypes.string,
        changeDateTimeModalIntroMessage: PropTypes.string,
        onHideDateTimeModal: PropTypes.func.isRequired,
        fetchSpotRates: PropTypes.func.isRequired,
        clearSelectedRate: PropTypes.func.isRequired,
        onClearCustomer: PropTypes.func.isRequired,
        onUserUpdate: PropTypes.func.isRequired,
        onUpdateData: PropTypes.func.isRequired,
        onShowDateTimeModal: PropTypes.func.isRequired,
        onHideRecommendedSpotsModal: PropTypes.func.isRequired,
        onHideRedundantMonthlyDetailsModal: PropTypes.func.isRequired,
        onHideRateDetailsModal: PropTypes.func.isRequired,
        onToggleOnlineCommuterDetailsModal: PropTypes.func.isRequired,
        onToggleOversizedFeeDetailsModal: PropTypes.func.isRequired,
        onToggleTimeExtendedDetailsModal: PropTypes.func.isRequired,
        onPurchase: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        changedDateTimeCount: PropTypes.number.isRequired,
        vehicleId: PropTypes.number,
    };
    state = {
        isFirstView: true,
        prevUser: this.props.user,
    };

    constructor(props) {
        super(props);

        const {
            match: {
                params: {spotId},
            },
        } = props;

        this._userUnsubmitted = false;
        this._firstLoad = true;
        this._spotId = parseInt(spotId, 10);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {user} = nextProps;
        const {prevUser} = prevState;

        if (prevUser.email !== user.email) {
            return {
                highlightAccountPanel: true,
                prevUser: user,
            };
        }

        return null;
    }

    componentDidMount() {
        const {
            apiDateTimeFormat,
            isMonthly,
            isAirport,
            starts,
            ends,
            rid,
            eventId,
            previousRental,
            onShowDateTimeModal,
            fetchSpotRates,
            onUpdateData,
            vehicleId,
        } = this.props;

        this._checkoutInitMoment = moment();

        fetchSpotRates({
            spotId: this._spotId,
            starts,
            ends: isMonthly
                ? moment(starts)
                      .add(1, 'month')
                      .format(apiDateTimeFormat)
                : ends,
            eventId,
            includeFacility: true,
            monthly: isMonthly,
            airport: isAirport,
            meta: {
                rid: parseInt(rid, 10),
                previousRental,
            },
            vehicleInfoId: vehicleId,
        })
            .then(response => {
                if (previousRental) {
                    onShowDateTimeModal({
                        title: 'Buy this Spot Again',
                    });
                }
            })
            .catch(error => {
                console.log('Checkout fetchSpotRates error:', error); // eslint-disable-line no-console
            });

        SegmentUtils.track({
            event: 'entered_checkout',
        });

        this._pageContainer = DOMUtils.el('body');
        this._bodyClasses = SHUtils.determineInitialLoadBodyClasses({
            pageType: 'checkout',
        });

        DOMUtils.addClass(this._pageContainer, this._bodyClasses);

        // Track "Availability Check" event when user lands directly on checkout page
        if (!includes(document.referrer, 'spothero.com')) {
            /* eslint-disable camelcase */
            SegmentUtils.track({
                event: 'Availability Check',
                properties: {
                    parking_type: isAirport
                        ? 'airport'
                        : isMonthly
                        ? 'monthly'
                        : 'transient',
                    search_start_time: moment(starts).toISOString(),
                    search_end_time: isMonthly
                        ? null
                        : moment(ends).toISOString(),
                    facility_id: this._spotId,
                    from_screen: 'checkout',
                    last_action: 'entered checkout',
                },
            });
            /* eslint-enable camelcase */
        }

        onUpdateData({
            changedDateTimeCount: 0,
        });

        GTMUtils.push({
            event: 'pageview',
            page: 'checkout',
            spotId: this._spotId,
        });

        SHUtils.staticScrollTo();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            isMonthly,
            user,
            checkoutError,
            selectedCreditCard,
            onUserUpdate,
            onUpdateData,
            onShowDateTimeModal,
            vehicleId,
            isAirport,
            starts,
            ends,
            rid,
            spot,
            eventId,
            fetchSpotRates,
            apiDateTimeFormat,
        } = this.props;
        const {
            isMonthly: prevMonthly,
            user: prevUser,
            checkoutError: prevCheckoutError,
            vehicleId: prevVehicleId,
            spot: prevSpot,
        } = prevProps;

        if (
            (!prevSpot && spot) ||
            (spot && prevSpot && spot.selectedRate !== prevSpot.selectedRate)
        ) {
            this._firstLoad = false;
        }

        if (prevVehicleId !== vehicleId) {
            fetchSpotRates({
                spotId: this._spotId,
                starts,
                ends: isMonthly
                    ? moment(starts)
                          .add(1, 'month')
                          .format(apiDateTimeFormat)
                    : ends,
                eventId,
                includeFacility: true,
                monthly: isMonthly,
                airport: isAirport,
                meta: {
                    rid: parseInt(rid, 10),
                },
                vehicleInfoId: vehicleId,
            }).catch(error => {
                console.log('Checkout fetchSpotRates() error:', error); // eslint-disable-line no-console
            });

            /* eslint-disable camelcase */
            SegmentUtils.track({
                event: 'Availability Check',
                properties: {
                    parking_type: isAirport
                        ? 'airport'
                        : isMonthly
                        ? 'monthly'
                        : 'transient',
                    search_start_time: moment(starts).toISOString(),
                    search_end_time: isMonthly
                        ? null
                        : moment(ends).toISOString(),
                    facility_id: this._spotId,
                    from_screen: 'checkout',
                    last_action: 'updated vehicle',
                },
            });
            /* eslint-enable camelcase */
        }

        if (prevMonthly !== isMonthly) {
            AnimationUtils.scrollTo(document.body, 0);

            if (!isMonthly) {
                onShowDateTimeModal();
            }
        }

        if (
            prevState.highlightAccountPanel !== this.state.highlightAccountPanel
        ) {
            this._clearHighlightTimeout();
            this._highlightTimeout = setTimeout(() => {
                this.setState({
                    highlightAccountPanel: false,
                });
            }, 3000);
        }

        if (!prevCheckoutError && checkoutError) {
            if (checkoutError.errors.length) {
                if (
                    some(checkoutError.errors, item => includes(item, 'card'))
                ) {
                    const declinedCard = find(user.creditCards, [
                        'id',
                        selectedCreditCard,
                    ]);

                    if (declinedCard) {
                        const declinedCardIndex = indexOf(
                            user.creditCards,
                            declinedCard
                        );

                        declinedCard.disabled = true;
                        declinedCard.isDefault = false;

                        user.creditCards.splice(
                            declinedCardIndex,
                            1,
                            declinedCard
                        );

                        CreditCardAPI.setAsDefault({
                            userId: user.id,
                            cardId: selectedCreditCard,
                            isDefault: false,
                        }).catch(error => {
                            // eslint-disable-next-line no-console
                            console.log(
                                'Card not found, could not update default status.'
                            );
                        });
                    }

                    onUserUpdate(user);
                    onUpdateData({
                        selectedCreditCard: 'new',
                    });
                }
            }
        }

        // this._userUnsubmitted indicates Pay and Reserve was clicked with unsubmitted user/guest login info
        // in this case, the first time _purchase runs the user will be logged in only, without reserving
        // so we programatically call _purchase a second time to ensure the reservation process runs
        if (this._userUnsubmitted && user.email !== prevUser.email) {
            this._userUnsubmitted = false;

            setTimeout(() => {
                // delay this slightly so that the data has a chance to update after a login is executed
                // before trying to re-purchase or else it will try to log the user in again and fail
                this._purchase();
            }, 100);
        }
    }

    componentWillUnmount() {
        const {onClearCustomer, clearSelectedRate} = this.props;

        DOMUtils.removeClass(this._pageContainer, this._bodyClasses);

        this._clearHighlightTimeout();

        onClearCustomer();
        clearSelectedRate();
    }

    _onBackToSearchClick = evt => {
        evt.preventDefault();

        const {latitude, longitude, starts, ends, eventId, pushTo} = this.props;
        const previousSearch = StorageUtils.get('previous-search', 'session');
        let url = `/search?starts=${starts}&ends=${ends}`;

        if (latitude && longitude) {
            url += `&latitude=${latitude}&longitude`;
        }

        if (eventId) {
            url += `&eid=${eventId}`;
        }

        if (previousSearch) {
            StorageUtils.remove('previous-search', 'session');

            url = previousSearch.url;
        }

        pushTo(url);
    };

    _onReservationDetailsContinue = () => {
        this.setState({
            isFirstView: false,
        });
    };

    _onReserveClick = (paymentRequestData = null) => {
        // paymentRequestData is used for Apple/Google Pay and contains the paymentOverlay (which has the token.id)
        // and stripePaymentType otherwise will be null for regular purchase
        this._purchase(paymentRequestData);
    };

    _onVehicleChange = ({
        value,
        make = null,
        model = null,
        year = null,
        isNew = false,
    }) => {
        const {onUpdateData} = this.props;
        const vehicleId =
            value !== 'new' && value !== 'DONTKNOW'
                ? parseInt(value, 10)
                : null;

        onUpdateData({
            isNewVehicle: isNew,
            vehicleId,
            vehicleMake: make,
            vehicleModel: model,
            vehicleYear: year,
        });
    };

    _onChangeDateTimeModalChange = ({start, end, meta}) => {
        const {
            apiDateTimeFormat,
            isMonthly,
            isAirport,
            previousRental,
            fetchSpotRates,
            vehicleId,
        } = this.props;
        const starts = start.format(apiDateTimeFormat);
        const ends = isMonthly
            ? start
                  .clone()
                  .add(1, 'month')
                  .format(apiDateTimeFormat)
            : end.format(apiDateTimeFormat);

        fetchSpotRates({
            spotId: this._spotId,
            includeFacility: true,
            starts,
            ends,
            monthly: isMonthly,
            airport: isAirport,
            meta: {
                previousRental,
                dateTimeChanged: true,
            },
            vehicleInfoId: vehicleId,
        }).catch(error => {
            console.log('fetchSpotRates error:', error); // eslint-disable-line no-console
        });

        /* eslint-disable camelcase */
        SegmentUtils.track({
            event: 'Availability Check',
            properties: {
                parking_type: isAirport
                    ? 'airport'
                    : isMonthly
                    ? 'monthly'
                    : 'transient',
                search_start_time: moment(starts).toISOString(),
                search_end_time: isMonthly ? null : moment(ends).toISOString(),
                facility_id: this._spotId,
                from_screen: 'checkout',
                last_action: previousRental
                    ? 'quick rebook'
                    : 'update requested times',
            },
        });
        /* eslint-enable camelcase */
    };

    _onCompleteRedundantMonthlyReservation = () => {
        this._purchase({
            forceRedundantMonthly: true,
        });
    };

    _clearHighlightTimeout() {
        if (this._highlightTimeout) {
            clearTimeout(this._highlightTimeout);
        }
    }

    _submitReservationDetails = () => {
        const data = {};

        if (this._reservationDetails) {
            data.reservation = this._reservationDetails.submit();

            if (!data.reservation) {
                AnimationUtils.scrollTo(
                    'html',
                    DOMUtils.getDocumentOffset('.CheckoutPanel-reservation').top
                );
            }
        }

        return data;
    };

    _submitForms() {
        const formData = this._submitReservationDetails();

        if (this._paymentInformation) {
            formData.payment = this._paymentInformation.submit();
        }

        return formData;
    }

    _purchase({
        // the payment overlay is passed through if Apple or Google Pay is used, it contains token.id and complete('success/fail')
        paymentOverlay = null,
        // again, if Apple or Google Pay, Stripe payment type is passed through based on which is used
        stripePaymentType = 'cc',
        forceRedundantMonthly = false,
    } = {}) {
        const {
            user: {isAdmin, status, email},
            promoCode,
            onPurchase,
            purchaseForCustomer,
            isRebookingReservation,
            isMonthly,
            isAirport,
            cityDisplayName,
            changedDateTimeCount,
            spot,
            starts,
            ends,
            vehicleId,
        } = this.props;
        const {reservation, payment} = this._submitForms();
        const oversizeFee = CheckoutUtils.getOversizeFeeFromPriceBreakdown(
            spot.selectedRate.price_breakdown
        );

        if (status === UserUtils.AUTH_STATE.LOGGED_OUT) {
            this._userUnsubmitted = true;
        }

        if (this._reservationDetails && !reservation) {
            return;
        } else if (
            this._paymentInformation &&
            !payment &&
            !purchaseForCustomer &&
            !isRebookingReservation
        ) {
            AnimationUtils.scrollTo(
                'html',
                DOMUtils.getDocumentOffset('.CheckoutPanel-payment').top
            );
        } else {
            const resPromoCode = promoCode || payment.promoCode || null;
            const userLatLng = StorageUtils.get('sh-user-location', 'session');
            const eventProps = {
                email,
                'promo code used': Boolean(resPromoCode),
                'checkout started to purchase': moment().diff(
                    this._checkoutInitMoment,
                    'seconds'
                ),
                city: cityDisplayName,
                'parking type': isAirport
                    ? 'airport'
                    : isMonthly
                    ? 'monthly'
                    : 'transient',
                'date/time update count': changedDateTimeCount || 0,
                'search start time': moment(starts).toISOString(),
                'search end time': isMonthly
                    ? null
                    : moment(ends).toISOString(),
                ...(vehicleId && {oversized_vehicle: Boolean(oversizeFee)}), // eslint-disable-line camelcase
                ...(userLatLng && {user_location: userLatLng}), // eslint-disable-line camelcase
                ...(resPromoCode && {'promo code string': resPromoCode.code}),
            };

            SegmentUtils.track({
                event: 'Checkout Step Completed',
                properties: eventProps,
            });

            onPurchase({
                reservationData: {
                    ...reservation,
                    ...payment,
                    paymentOverlay,
                    stripePaymentType,
                    forceRedundantMonthly,
                },
                isAdmin,
            });
        }
    }

    _renderUnavailable() {
        return (
            <Card className="Checkout-spot-information-unavailable">
                <p>
                    No spots are available at this time but we don&apos;t want
                    you going spotless...
                </p>
                <Button
                    className="Button-space-after"
                    color="primary"
                    onClick={this.props.onShowDateTimeModal}
                >
                    Change Date/Time
                </Button>
                <span>or</span>
                <Button
                    className="Button-space-before"
                    color="secondary"
                    onClick={this._onBackToSearchClick}
                >
                    Return to Search
                </Button>
            </Card>
        );
    }

    render() {
        const {
            user: {isAdmin},
            isLoadingRates,
            isPurchasing,
            isPurchasingWithPaymentRequest,
            spot,
            starts,
            ends,
            isMonthly,
            phoneNumber,
            phoneNumberFriendly,
            monthlyPhoneNumber,
            monthlyPhoneNumberFriendly,
            cityDisplayName,
            cityTimezone,
            showChangeDateTimeModal,
            recommendedSpotsUnavailableReason,
            showRecommendedSpotsModal,
            onHideRecommendedSpotsModal,
            showRedundantMonthlyReservationModal,
            onHideRedundantMonthlyDetailsModal,
            redundantMonthlyReservationModalDate,
            redundantMonthlyReservationModalParkingPassURL,
            showRateDetailsModal,
            rateDetailsModalSection,
            showOnlineCommuterRateModal,
            showOversizedFeeDetailsModal,
            showWhyTimeExtendedModal,
            fetchSpotRates,
            onHideRateDetailsModal,
            onToggleOnlineCommuterDetailsModal,
            onToggleOversizedFeeDetailsModal,
            onToggleTimeExtendedDetailsModal,
            changeDateTimeModalTitle,
            changeDateTimeModalIntroMessage,
            onHideDateTimeModal,
        } = this.props;
        const {isFirstView} = this.state;

        if (!spot) {
            return <SHLoader className="Checkout-loader" />;
        }

        const cancellationAllowed =
            get(spot, 'facility.cancellation_allowed') !== 'no';

        return (
            <div className="Checkout">
                <CheckoutMeta cityDisplayName={cityDisplayName} spot={spot} />
                <div className="container">
                    {isLoadingRates && this._firstLoad ? (
                        <SHLoader className="Checkout-loader" />
                    ) : (
                        <Fragment>
                            <div className="Checkout-spot-information">
                                {spot.selectedRate ? (
                                    <ReservationSummary
                                        isLoadingRates={isLoadingRates}
                                    />
                                ) : (
                                    this._renderUnavailable()
                                )}
                                <CheckoutValueProps
                                    className="CheckoutValueProps-sidebar"
                                    showCancellationValueProp={
                                        cancellationAllowed
                                    }
                                />
                                <CheckoutValueProps
                                    className="CheckoutValueProps-footer"
                                    showCancellationValueProp={
                                        cancellationAllowed
                                    }
                                />
                            </div>
                            <div className="Checkout-reservation CheckoutPanel-group">
                                <AccountInformation
                                    ref={node => {
                                        this._accountInformation = node;
                                    }}
                                />
                                <ReservationDetails
                                    ref={node => {
                                        this._reservationDetails = node;
                                    }}
                                    isFirstView={isFirstView}
                                    onContinue={
                                        this._onReservationDetailsContinue
                                    }
                                    onVehicleChange={this._onVehicleChange}
                                    isLoadingRates={isLoadingRates}
                                />
                                <PaymentInformation
                                    ref={node => {
                                        this._paymentInformation = node;
                                    }}
                                    isFirstView={isFirstView}
                                    isLoadingRates={isLoadingRates}
                                    validateReservationInfo={
                                        this._submitReservationDetails
                                    }
                                    onReserveClick={this._onReserveClick}
                                />
                            </div>
                        </Fragment>
                    )}
                </div>
                {showChangeDateTimeModal && (
                    <ChangeDateTimeModal
                        title={changeDateTimeModalTitle}
                        introMessage={changeDateTimeModalIntroMessage}
                        isMonthly={isMonthly}
                        starts={starts}
                        ends={ends}
                        showOtherOptions={!spot.selectedRate}
                        onChange={this._onChangeDateTimeModalChange}
                        onHidden={onHideDateTimeModal}
                    />
                )}
                {showRateDetailsModal && (
                    <RateDetailsModal
                        section={rateDetailsModalSection}
                        spot={spot}
                        onHidden={onHideRateDetailsModal}
                    />
                )}
                {showOnlineCommuterRateModal && (
                    <OnlineCommuterRateModal
                        description={
                            spot.selectedRate.online_commuter_rate_description
                        }
                        onHidden={onToggleOnlineCommuterDetailsModal}
                    />
                )}
                {showOversizedFeeDetailsModal && (
                    <OversizedFeeDetailsModal
                        spot={spot}
                        onHidden={onToggleOversizedFeeDetailsModal}
                    />
                )}
                {showWhyTimeExtendedModal && (
                    <WhyTimeExtendedModal
                        requestedStart={starts}
                        requestedEnd={ends}
                        rate={spot.selectedRate}
                        onHidden={onToggleTimeExtendedDetailsModal}
                    />
                )}
                {showRecommendedSpotsModal && (
                    <RecommendedSpotsModal
                        isAdmin={isAdmin}
                        latitude={spot.latitude}
                        longitude={spot.longitude}
                        starts={starts}
                        ends={ends}
                        isMonthly={isMonthly}
                        timezone={cityTimezone}
                        recommendedSpotsUnavailableReason={
                            recommendedSpotsUnavailableReason
                        }
                        onHidden={onHideRecommendedSpotsModal}
                        onBackToSearchClick={this._onBackToSearchClick}
                        fetchSpotRates={fetchSpotRates}
                    />
                )}
                {showRedundantMonthlyReservationModal && (
                    <RedundantMonthlyReservationModal
                        onHidden={onHideRedundantMonthlyDetailsModal}
                        reservationCreationDateFormatted={
                            redundantMonthlyReservationModalDate
                        }
                        parkingPassURL={
                            redundantMonthlyReservationModalParkingPassURL
                        }
                        contactNumber={monthlyPhoneNumber || phoneNumber}
                        contactNumberFriendly={
                            monthlyPhoneNumberFriendly || phoneNumberFriendly
                        }
                        onCompleteReservation={
                            this._onCompleteRedundantMonthlyReservation
                        }
                    />
                )}
                {isPurchasing && (
                    <FullPageLoader
                        showAnimation={!isPurchasingWithPaymentRequest}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            isPurchasing,
            isFetchingRates,
            checkoutError,
            rid,
            promoCode,
            previousRental,
            recommendedSpotsUnavailableReason,
            showChangeDateTimeModal,
            changeDateTimeModalTitle,
            changeDateTimeModalIntroMessage,
            showRecommendedSpotsModal,
            showRedundantMonthlyReservationModal,
            redundantMonthlyReservationModalDate,
            redundantMonthlyReservationModalParkingPassURL,
            showWhyTimeExtendedModal,
            purchaseForCustomer,
            isRebookingReservation,
            changedDateTimeCount,
            vehicleId,
            isPurchasingWithPaymentRequest,
        },
        city: {
            data: {
                phone_number: phoneNumber,
                phone_number_friendly: phoneNumberFriendly,
                monthly_phone_number: monthlyPhoneNumber,
                monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
                display_name: cityDisplayName,
                timezone,
            },
        },
        searchRequest: {
            monthly,
            airport,
            starts,
            ends,
            latitude,
            longitude,
            eid,
        },
        spot: {
            selected,
            showRateDetailsModal,
            rateDetailsModalSection,
            showOversizedFeeDetailsModal,
            showOnlineCommuterRateModal,
        },
        user: {data: user},
    } = state;

    return {
        isLoadingRates: isFetchingRates,
        isPurchasing,
        user,
        checkoutError,
        isMonthly: monthly,
        isAirport: airport,
        starts,
        ends,
        latitude,
        longitude,
        eventId: eid,
        rid,
        promoCode,
        previousRental,
        spot: selected,
        showChangeDateTimeModal,
        changeDateTimeModalTitle,
        changeDateTimeModalIntroMessage,
        showRateDetailsModal,
        rateDetailsModalSection,
        showOversizedFeeDetailsModal,
        showOnlineCommuterRateModal,
        phoneNumber,
        phoneNumberFriendly,
        monthlyPhoneNumber,
        monthlyPhoneNumberFriendly,
        cityDisplayName,
        cityTimezone: timezone,
        recommendedSpotsUnavailableReason,
        showRecommendedSpotsModal,
        showRedundantMonthlyReservationModal,
        redundantMonthlyReservationModalDate,
        redundantMonthlyReservationModalParkingPassURL,
        showWhyTimeExtendedModal,
        purchaseForCustomer,
        isRebookingReservation,
        changedDateTimeCount,
        vehicleId,
        isPurchasingWithPaymentRequest,
    };
};

const mapDispatchToProps = {
    fetchSpotRates: fetchRates,
    clearSelectedRate: resetSelectedRate,
    onHideDateTimeModal: hideChangeDateTime,
    onShowDateTimeModal: showChangeDateTime,
    onHideRecommendedSpotsModal: hideRecommendedSpots,
    onHideRedundantMonthlyDetailsModal: hideRedundantMonthlyDetails,
    onHideRateDetailsModal: hideRateDetails,
    onToggleOnlineCommuterDetailsModal: toggleOnlineCommuterDetails,
    onToggleOversizedFeeDetailsModal: toggleOversizedFeeDetails,
    onToggleTimeExtendedDetailsModal: toggleTimeExtendedDetails,
    onClearCustomer: clearCustomer,
    onUserUpdate: userUpdate,
    onUpdateData: updateData,
    onPurchase: purchase,
    pushTo: push,
};

const enhance = compose(
    withConfig(['apiDateTimeFormat']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(Checkout);
